import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  ThemeProvider,
  Typography,
  createTheme,
} from "@mui/material";
import ForestIcon from "@mui/icons-material/Forest";
import OpacityIcon from "@mui/icons-material/Opacity";
import WarningIcon from "@mui/icons-material/Warning";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useProject } from "../api/project";
import {
  useUpdateVegetationClass,
  useUpdateModelClass,
  useUpdateModelSubclass,
  useUpdateFuelParameters,
  useVegetationDetail,
} from "../api/vegetation";
import { useVegetationTypes } from "../api/vegetationType";
import { hasSubclass, subclassSpecFor } from "../constants/modelSubclass";
import { useUnitSystem } from "../hooks/useUnitSystem";
import { formatFuelLoad } from "../utils/units";

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    background: { paper: "#212b37" },
  },
});

const UNCHANGED = "";

interface VegEditValues {
  vegClass: string;
  modelClass: string;
  modelSubclass: number | "";
  lmax: number;
  k: number;
}

const MULTI_DEFAULTS: VegEditValues = {
  vegClass: UNCHANGED,
  modelClass: UNCHANGED,
  modelSubclass: "",
  lmax: 20,
  k: 0.1,
};

/**
 * Details of a clicked polygon that was written into the vegetation tiles by
 * a revegetation treatment. No Vegetation row backs it, so it can only be
 * displayed, not edited — changes go through the treatment itself.
 */
export interface RevegFeatureInfo {
  vegClass: string;
  lmax?: number;
  k?: number;
  yearsSinceReveg?: number;
}

interface VegetationMultiEditModalProps {
  /** Vegetation primary keys to apply the edits to. One id = single edit. */
  ids: number[];
  /**
   * True when all ids belong to ONE clicked polygon: the vegetation tiles are
   * seam-dissolved, so a single visible polygon can be backed by several
   * Vegetation rows with identical values. Renders the single-polygon UI
   * (pre-filled from the first id) while still applying edits to every row.
   */
  singlePolygon?: boolean;
  /** When set (and ids is empty), render read-only revegetation details. */
  revegInfo?: RevegFeatureInfo | null;
  onClose: () => void;
}

/**
 * Editor for vegetation polygons — used for both a single polygon and a
 * Shift-multi-selected set. It exposes the same edits the old popover had
 * (vegetation class, model class + Scott & Burgan subclass, fuel-load
 * parameters) and applies whatever changed to every selected polygon via
 * looped single-id PATCHes (there is no bulk endpoint).
 *
 * - Single edit: the polygon's current values are fetched and pre-filled, and
 *   only fields the user actually changes are PATCHed.
 * - Multi edit: fields start "unchanged" and only the ones the user sets are
 *   applied to all selected polygons.
 */
const VegetationMultiEditModal: React.FC<VegetationMultiEditModalProps> = ({
  ids,
  singlePolygon,
  revegInfo,
  onClose,
}) => {
  const unitSystem = useUnitSystem();
  const { project } = useProject();
  const { mutateAsync: updateVegClass } = useUpdateVegetationClass();
  const { mutateAsync: updateModelClass } = useUpdateModelClass();
  const { mutateAsync: updateModelSubclass } = useUpdateModelSubclass();
  const { mutateAsync: updateFuelParameters } = useUpdateFuelParameters();

  // Seam-dissolve members share identical values, so a single clicked polygon
  // hydrates from its first member and edits apply to all of them.
  const isSingle = ids.length === 1 || (!!singlePolygon && ids.length > 0);
  const { data: detail, isLoading: detailLoading } = useVegetationDetail(
    isSingle ? ids[0] : null,
    isSingle
  );

  const [vegClass, setVegClass] = useState<string>(MULTI_DEFAULTS.vegClass);
  const [modelClass, setModelClass] = useState<string>(MULTI_DEFAULTS.modelClass);
  const [modelSubclass, setModelSubclass] = useState<number | "">(MULTI_DEFAULTS.modelSubclass);
  const [lmax, setLmax] = useState(MULTI_DEFAULTS.lmax);
  const [k, setK] = useState(MULTI_DEFAULTS.k);
  const [fuelTouched, setFuelTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Baseline used to decide which fields actually changed.
  const initialRef = useRef<VegEditValues>(MULTI_DEFAULTS);
  // For a single polygon we can only edit once its current values have loaded.
  const [hydrated, setHydrated] = useState(!isSingle);

  useEffect(() => {
    if (!isSingle || !detail) return;
    const next: VegEditValues = {
      vegClass: detail.VegClass ?? "",
      modelClass: detail.model_class_name ?? "",
      modelSubclass: typeof detail.model_subclass === "number" ? detail.model_subclass : "",
      lmax: detail.lmax ?? 20,
      k: detail.k ?? 0.1,
    };
    initialRef.current = next;
    setVegClass(next.vegClass);
    setModelClass(next.modelClass);
    setModelSubclass(next.modelSubclass);
    setLmax(next.lmax);
    setK(next.k);
    setHydrated(true);
  }, [isSingle, detail]);

  const vegClasses = project?.data?.veg_classes ?? [];
  // A polygon can carry a class name missing from the project's list (e.g. its
  // type was renamed before the backend cascaded renames to features, #239) —
  // include it so the Select shows the stale value instead of rendering blank.
  const vegClassOptions =
    vegClass && !vegClasses.includes(vegClass) ? [vegClass, ...vegClasses] : vegClasses;
  const modelClasses = project?.data?.model_classes ?? [];
  const vegTypesQ = useVegetationTypes();
  const vegTypes = vegTypesQ.data?.data ?? [];

  // Selecting a vegetation class pulls in that class's project defaults for
  // the model class and fuel-load parameters (#194). The baselines are moved
  // with the fields so the defaults don't count as explicit user edits — on
  // save, the backend resets the feature to the new class's defaults anyway;
  // only values the user changes AFTER picking the class are PATCHed on top.
  const handleVegClassChange = (next: string) => {
    setVegClass(next);
    const vt = vegTypes.find((t) => t.veg_class === next);
    if (!vt) return;
    const nextValues: Omit<VegEditValues, "vegClass"> = {
      modelClass: vt.model_class_name ?? "",
      modelSubclass: typeof vt.model_subclass === "number" ? vt.model_subclass : "",
      lmax: vt.max_fuel_load ?? 20,
      k: vt.accumulation_rate ?? 0.1,
    };
    setModelClass(nextValues.modelClass);
    setModelSubclass(nextValues.modelSubclass);
    setLmax(nextValues.lmax);
    setK(nextValues.k);
    setFuelTouched(false);
    initialRef.current = { ...initialRef.current, ...nextValues };
  };

  const previewCurveData = (() => {
    const points: { age: number; fuel: number }[] = [];
    for (let age = 0; age <= 50; age += 1) {
      points.push({ age, fuel: lmax * (1 - Math.exp(-k * age)) });
    }
    return points;
  })();

  const init = initialRef.current;
  const nextSubclass = hasSubclass(modelClass)
    ? modelSubclass === ""
      ? null
      : Number(modelSubclass)
    : null;
  const initSubclass = init.modelSubclass === "" ? null : Number(init.modelSubclass);

  const applyVeg = !!vegClass && vegClass !== init.vegClass;
  const modelClassChanged = !!modelClass && modelClass !== init.modelClass;
  const subclassChanged = hasSubclass(modelClass) && nextSubclass !== initSubclass;
  const applyFuel = fuelTouched && (lmax !== init.lmax || k !== init.k);

  const subclassRequired = hasSubclass(modelClass) && modelSubclass === "";
  const hasChange = applyVeg || modelClassChanged || subclassChanged || applyFuel;
  const canSubmit = hydrated && !isSubmitting && !subclassRequired && hasChange;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      // Veg-class changes land first: the backend resets the model class and
      // fuel params to the new class's defaults (#194), so any explicit
      // selections below must be applied after it, not race it.
      if (applyVeg) {
        await Promise.all(ids.map((id) => updateVegClass({ id, veg_class: vegClass })));
      }
      const tasks: Promise<unknown>[] = [];
      for (const id of ids) {
        if (modelClassChanged) {
          // Two PATCHes — the backend treats model_class_name and
          // model_subclass as separate edits (same as the single popover).
          tasks.push(
            updateModelClass({ id, model_class_name: modelClass }).then(() =>
              updateModelSubclass({ id, model_subclass: nextSubclass })
            )
          );
        } else if (subclassChanged) {
          tasks.push(updateModelSubclass({ id, model_subclass: nextSubclass }));
        }
        if (applyFuel) {
          tasks.push(
            updateFuelParameters({ id, max_fuel_load: lmax, accumulation_rate: k })
          );
        }
      }
      await Promise.all(tasks);
      onClose();
    } catch (err) {
      console.error("Vegetation edit failed:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectSx = {
    color: "white",
    ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255, 255, 255, 0.5)" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
    ".MuiSvgIcon-root": { color: "white" },
  };

  const busy = isSubmitting || (isSingle && detailLoading && !hydrated);

  // Read-only branch: a polygon painted into the vegetation layer by a
  // revegetation treatment. There is no Vegetation row to edit — the class
  // and parameters live on the treatment.
  if (ids.length === 0 && revegInfo) {
    const revegLmax = revegInfo.lmax ?? 20;
    const revegK = revegInfo.k ?? 0.1;
    const revegAge = revegInfo.yearsSinceReveg;
    const revegCurve: { age: number; fuel: number }[] = [];
    for (let age = 0; age <= 50; age += 1) {
      revegCurve.push({ age, fuel: revegLmax * (1 - Math.exp(-revegK * age)) });
    }
    const currentLoad =
      revegAge != null ? revegLmax * (1 - Math.exp(-revegK * revegAge)) : null;

    return (
      <Dialog
        open
        onClose={onClose}
        PaperProps={{ sx: { bgcolor: "#212b37", color: "white", width: 520, maxWidth: "90vw" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", fontWeight: "bold" }}>
          <ForestIcon sx={{ mr: 1 }} />
          Revegetated Area
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.7)" }}>
            This vegetation comes from a revegetation treatment in the current
            strategy. To change its vegetation type or dates, edit the
            treatment itself.
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Vegetation Class: <strong>{revegInfo.vegClass || "—"}</strong>
          </Typography>
          {revegAge != null && (
            <Typography variant="body2" sx={{ mb: 1, color: "rgba(255,255,255,0.7)" }}>
              Age at scenario date: {revegAge.toFixed(1)} years
              {currentLoad != null &&
                ` — estimated fuel load ${formatFuelLoad(currentLoad, unitSystem)}`}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mb: 1, color: "rgba(255,255,255,0.7)" }}>
            Max Fuel Load (lmax): {formatFuelLoad(revegLmax, unitSystem)}
            {" · "}Accumulation Rate (k): {revegK.toFixed(3)}
          </Typography>
          <Box sx={{ height: 100, mt: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={revegCurve} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <XAxis dataKey="age" />
                <YAxis domain={[0, Math.max(30, revegLmax)]} />
                <Tooltip
                  formatter={(v: number) => formatFuelLoad(v, unitSystem)}
                  labelFormatter={(l) => `age: ${l} yr`}
                  wrapperStyle={{ outline: "none" }}
                  contentStyle={{ background: "rgba(0,0,0,0)", border: "none", boxShadow: "none" }}
                />
                <Line type="monotone" dataKey="fuel" stroke="#00d4ad" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onClose={() => {
        if (!isSubmitting) onClose();
      }}
      PaperProps={{ sx: { bgcolor: "#212b37", color: "white", width: 520, maxWidth: "90vw" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", fontWeight: "bold" }}>
        <ForestIcon sx={{ mr: 1 }} />
        {isSingle ? "Edit Vegetation Polygon" : `Edit ${ids.length} Vegetation Polygons`}
      </DialogTitle>

      <DialogContent sx={{ position: "relative" }}>
        {busy && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(33,43,55,0.8)",
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <CircularProgress color="inherit" />
            <span>{isSubmitting ? "Saving…" : "Loading parameters…"}</span>
          </Box>
        )}

        <Typography
          variant="body2"
          sx={{ mb: 2, color: "orange", display: "flex", alignItems: "center" }}
        >
          <WarningIcon sx={{ mr: 1, fontSize: "1.5rem" }} />
          {isSingle
            ? "Changes apply to this polygon only. Changing the vegetation class affects all strategies in the project."
            : `Each field you change below is applied to all ${ids.length} selected polygons. Leave a field unchanged to keep each polygon's existing value.`}
        </Typography>

        <ThemeProvider theme={darkTheme}>
          {/* Vegetation class */}
          <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
            <InputLabel id="veg-class-label" sx={{ color: "white" }}>
              Vegetation Class
            </InputLabel>
            <Select
              labelId="veg-class-label"
              value={vegClass}
              label="Vegetation Class"
              onChange={(e) => handleVegClassChange(e.target.value as string)}
              sx={selectSx}
            >
              {!isSingle && (
                <MenuItem value={UNCHANGED}>
                  <em>Leave unchanged</em>
                </MenuItem>
              )}
              {vegClassOptions.map((veg) => (
                <MenuItem key={veg} value={veg}>
                  {veg}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Model class (+ Scott & Burgan subclass) */}
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="model-class-label" sx={{ color: "white" }}>
              Model Class
            </InputLabel>
            <Select
              labelId="model-class-label"
              value={modelClass}
              label="Model Class"
              onChange={(e) => {
                const next = e.target.value as string;
                setModelClass(next);
                if (!hasSubclass(next)) setModelSubclass("");
              }}
              sx={selectSx}
            >
              {!isSingle && (
                <MenuItem value={UNCHANGED}>
                  <em>Leave unchanged</em>
                </MenuItem>
              )}
              {modelClasses.map((mc) => (
                <MenuItem key={mc} value={mc}>
                  {mc}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {(() => {
            const spec = subclassSpecFor(modelClass);
            if (!spec) return null;
            return (
            <FormControl fullWidth size="small" sx={{ mb: 2.5 }} error={subclassRequired}>
              <InputLabel id="model-subclass-label" sx={{ color: "white" }}>
                {spec.label}
              </InputLabel>
              <Select
                labelId="model-subclass-label"
                value={modelSubclass === "" ? "" : modelSubclass}
                label={spec.label}
                onChange={(e) => {
                  const v = e.target.value;
                  setModelSubclass(v === "" ? "" : Number(v));
                }}
                sx={selectSx}
              >
                {spec.options.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            );
          })()}
        </ThemeProvider>

        {/* Fuel-load parameters — always expanded */}
        <Box sx={{ mt: 1 }}>
          <Typography
            variant="body2"
            sx={{ mb: 1, display: "flex", alignItems: "center", fontWeight: 600 }}
          >
            <OpacityIcon sx={{ mr: 1, fontSize: 18 }} />
            Fuel Load Parameters
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: "rgba(255,255,255,0.7)" }}>
            Max Fuel Load (lmax): {formatFuelLoad(lmax, unitSystem)}
          </Typography>
          <Slider
            value={lmax}
            onChange={(_, v) => {
              setLmax(v as number);
              setFuelTouched(true);
            }}
            min={0}
            max={50}
            step={0.5}
            sx={{ color: "#00d4ad" }}
          />
          <Typography variant="body2" sx={{ mt: 2, mb: 1, color: "rgba(255,255,255,0.7)" }}>
            Accumulation Rate (k): {k.toFixed(3)}
          </Typography>
          <Slider
            value={k}
            onChange={(_, v) => {
              setK(v as number);
              setFuelTouched(true);
            }}
            min={0.01}
            max={0.5}
            step={0.01}
            sx={{ color: "#00d4ad" }}
          />
          <Box sx={{ height: 100, mt: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={previewCurveData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <XAxis dataKey="age" />
                <YAxis domain={[0, Math.max(30, lmax)]} />
                <Tooltip
                  formatter={(v: number) => formatFuelLoad(v, unitSystem)}
                  labelFormatter={(l) => `age: ${l} yr`}
                  wrapperStyle={{ outline: "none" }}
                  contentStyle={{ background: "rgba(0,0,0,0)", border: "none", boxShadow: "none" }}
                />
                <Line type="monotone" dataKey="fuel" stroke="#00d4ad" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={{ bgcolor: "#00d4ad", "&:hover": { bgcolor: "#00b89a" } }}
        >
          {isSingle ? "Save" : `Apply to ${ids.length}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VegetationMultiEditModal;
